import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { ar } from "./ar";
import { en } from "./en";

export type Lang = "ar" | "en";
type Dict = Record<keyof typeof ar, string>;

const DICTS: Record<Lang, Dict> = { ar, en };

interface I18nCtx {
  lang: Lang;
  dir: "rtl" | "ltr";
  t: (key: keyof Dict) => string;
  setLang: (l: Lang) => void;
}

const Ctx = createContext<I18nCtx | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>(
    () => (localStorage.getItem("vira.lang") as Lang) || "en",
  );

  useEffect(() => {
    localStorage.setItem("vira.lang", lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);

  const value: I18nCtx = {
    lang,
    dir: lang === "ar" ? "rtl" : "ltr",
    t: (key) => DICTS[lang][key] ?? (key as string),
    setLang,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18nCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
